import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { MessageSquareText } from "lucide-react";

/**
 * Prompts a client for 10-100 word feedback on their oldest completed
 * session that hasn't been reviewed yet. Dismissible here (re-appears on
 * next navigation) — the hard block is enforced server-side when they try
 * to JOIN their next session (POST /bookings/{id}/jaas-token returns 403).
 */
export default function FeedbackGate() {
  const { user } = useAuth();
  const [pending, setPending] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user || user.role !== "client") return;
    api.get("/feedback/pending")
      .then(({ data }) => setPending(data?.pending ? data : null))
      .catch(err => {
        if (err?.response?.status !== 401) console.warn("Feedback status check failed:", err?.message || err);
      });
  }, [user]);

  if (!pending || dismissed) return null;
  if (user?.rating_due) return null; // avoid stacking with the mandatory rating blocker
  const words = text.trim() ? text.trim().split(/\s+/).length : 0;
  const wordsOk = words >= 10 && words <= 100;

  const submit = async (e) => {
    e.preventDefault();
    if (!wordsOk) return;
    setBusy(true);
    try {
      await api.post(`/bookings/${pending.booking_id}/feedback`, { text: text.trim() });
      toast.success("Thanks for your feedback!");
      setPending(null);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="feedback-gate" className="fixed inset-0 z-[95] flex items-center justify-center p-4"
      style={{ background: "rgba(20,20,18,0.55)" }}>
      <div className="card-soft w-full max-w-lg p-7">
        <div className="flex items-start gap-3 mb-4">
          <MessageSquareText className="w-6 h-6 flex-shrink-0" style={{ color: "var(--brand)" }} />
          <div>
            <div className="font-serif-display text-xl">How was your session?</div>
            <div className="text-sm mt-1" style={{ color: "var(--text-secondary)" }}>
              Tell us about your {pending.service_name || "session"} with {pending.counselor_name}
              {pending.date ? ` on ${pending.date}` : ""}. This helps us and future clients.
            </div>
          </div>
        </div>
        <form onSubmit={submit}>
          <textarea data-testid="feedback-gate-textarea" value={text} onChange={e => setText(e.target.value)}
            rows={5} placeholder="Share a few thoughts about the session and your counselor…"
            className="w-full rounded-xl border border-[color:var(--border-soft)] px-4 py-3 focus:outline-none focus:border-[color:var(--brand)] resize-none" />
          <div className="flex items-center justify-between mt-2 text-xs" style={{ color: wordsOk ? "var(--brand)" : "var(--text-secondary)" }}>
            <span data-testid="feedback-gate-wordcount">{words} / 10–100 words</span>
            {!wordsOk && words > 0 && <span style={{ color: "var(--accent)" }}>{words < 10 ? "A little more detail, please" : "Please keep it under 100 words"}</span>}
          </div>
          <div className="flex items-center justify-end gap-3 mt-5">
            <button type="button" data-testid="feedback-gate-later" onClick={() => setDismissed(true)}
              className="text-sm underline" style={{ color: "var(--text-secondary)" }}>
              Remind me later
            </button>
            <button data-testid="feedback-gate-submit" disabled={!wordsOk || busy}
              className="btn-primary text-sm px-5 py-2.5 disabled:opacity-50 disabled:cursor-not-allowed">
              {busy ? "Submitting…" : "Submit feedback"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
